import cx from 'classnames';
import {isBoolean} from 'lodash';
import {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {ConfirmModal, Icon} from 'components';
import {ParticipantAsyncRequest, AsyncRequestStatusName, SumaEmailVerificationData} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {transformUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE.SUMA_EMAIL_VERIFICATION_MODAL;

type Props = {
    open: boolean;
    asyncRequest: ParticipantAsyncRequest<SumaEmailVerificationData>;
    onClose: (data: {reset: boolean}) => void;
};

export const SumaEmailVerificationModal: React.FC<Props> = ({open, asyncRequest, onClose}: Props) => {
    const {t} = useTranslation();
    const {success, failed} = useMemo(() => {
        const successStatus = asyncRequest?.status.name === AsyncRequestStatusName.Success;
        return {success: successStatus, failed: !successStatus};
    }, [asyncRequest]);
    const data = useMemo(() => asyncRequest?.data, [asyncRequest]);
    const verificationData = useMemo(() => data?.data, [data]);

    const handleClose = useCallback((accepted: boolean) => {
        onClose({reset: !accepted});
    }, [onClose]);

    const renderVerificationCheck = useCallback((value: boolean, label: string) => (
        isBoolean(value) && (
            <div className='tw-flex tw-items-center'>
                <Icon
                    name={value ? 'check-circle' : 'alert-circle'}
                    color={value ? 'success' : 'alert'}
                    size='sm'
                    className='tw-mr-2'
                />
                {`${label}: ${transformUtils.formatBoolean(t, value)}`}
            </div>
        )
    ), [t]);

    const renderVerificationDetails = useCallback(() => (
        <div>
            {verificationData?.email &&
                <div className='tw-ts-h5-bold tw-mb-4'>{`${t(T_KEYS.FIELDS.EMAIL)}: ${verificationData.email}`}</div>}
            <div className='tw-ts-h5 tw-mb-2'>{`${t(T_KEYS.VALIDATIONS)}:`}</div>
            <div className='tw-grid tw-gap-1'>
                {renderVerificationCheck(verificationData?.deliverable, t(T_KEYS.FIELDS.DELIVERABLE))}
            </div>
        </div>
    ), [t, verificationData, renderVerificationCheck]);

    return (
        <ConfirmModal
            open={open}
            fullWidth
            maxWidth='sm'
            okText={t(T_KEYS.DONE)}
            cancelText={t(T_KEYS.RESET)}
            closeTimeoutMS={0}
            onClose={handleClose}
        >
            <div className='tw-flex tw-flex-col tw-items-center'>
                {success ?
                    <Icon name='check-circle' color='success' size='8xl' thickness='sm' className='tw-self-center' /> :
                    <Icon name='x-circle' color='alert' size='8xl' thickness='sm' className='tw-self-center' />}
                <div className='tw-inline-block'>
                    <div className={cx('tw-mt-6 tw-ts-h2', {'tw-text-success': success, 'tw-text-alert': failed})}>
                        {t(success ? T_KEYS.TITLE_SUCCESS : T_KEYS.TITLE_FAILED)}
                    </div>
                    {verificationData?.email &&
                        <div className='tw-my-4'>
                            {renderVerificationDetails()}
                        </div>}
                </div>
            </div>
        </ConfirmModal>
    );
};
